import React, { useState, useEffect } from 'react';
import { Lock, Mail, Phone, Building, Calendar, Eye, CheckCircle, Clock, MessageSquare, LogOut, RefreshCw, User, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Check if already authenticated in session
  useEffect(() => {
    const authToken = sessionStorage.getItem('sragcircle_admin_auth');
    if (authToken) {
      setIsAuthenticated(true);
      fetchInquiries();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/verify`, { password });
      
      if (response.data.success) {
        sessionStorage.setItem('sragcircle_admin_auth', 'true');
        setIsAuthenticated(true);
        toast.success('Welcome to Admin Panel');
        fetchInquiries();
      }
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('sragcircle_admin_auth');
    setIsAuthenticated(false);
    setInquiries([]);
    setSelectedInquiry(null);
    toast.success('Logged out successfully');
  };

  const fetchInquiries = async () => {
    setIsRefreshing(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contact`);
      // Sort by created_at descending (newest first)
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setInquiries(sorted);
    } catch (error) {
      toast.error('Failed to fetch inquiries');
    } finally {
      setIsRefreshing(false);
    }
  };

  const updateStatus = async (inquiryId, newStatus) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/contact/${inquiryId}`, { status: newStatus });
      
      // Update local state
      setInquiries(prev => prev.map(inq => 
        inq.id === inquiryId ? { ...inq, status: newStatus } : inq
      ));
      
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry(prev => ({ ...prev, status: newStatus }));
      }
      
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-700 border-blue-200',
      read: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      responded: 'bg-green-100 text-green-700 border-green-200'
    };
    
    const icons = {
      new: <Clock className="w-3 h-3" />,
      read: <Eye className="w-3 h-3" />,
      responded: <CheckCircle className="w-3 h-3" />
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.new}`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a1a] via-[#0d1a2d] to-[#0a0a1a] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#0066CC]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-[#0066CC]" />
              </div>
              <h1 className="text-2xl font-bold text-[#1a1a5c]">Admin Access</h1>
              <p className="text-gray-500 mt-2">Enter password to continue</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="h-12 text-center text-lg tracking-widest"
                  autoFocus
                />
              </div>
              
              <Button
                type="submit"
                disabled={isLoading || !password}
                className="w-full bg-[#0066CC] hover:bg-[#0055AA] text-white h-12 text-lg font-semibold rounded-xl"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Verifying...
                  </span>
                ) : (
                  'Access Admin Panel'
                )}
              </Button>
            </form>
            
            <p className="text-center text-gray-400 text-sm mt-6">
              SRAGCircle OPC Admin Portal
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#0a0a1a] text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0066CC] rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Contact Inquiries</h1>
              <p className="text-gray-400 text-sm">SRAGCircle OPC Admin</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={fetchInquiries}
              variant="outline"
              size="sm"
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
              disabled={isRefreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white hover:bg-gray-800"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{inquiries.length}</p>
                <p className="text-gray-500 text-sm">Total Inquiries</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">
                  {inquiries.filter(i => i.status === 'new').length}
                </p>
                <p className="text-gray-500 text-sm">New</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-600">
                  {inquiries.filter(i => i.status === 'read').length}
                </p>
                <p className="text-gray-500 text-sm">Read</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">
                  {inquiries.filter(i => i.status === 'responded').length}
                </p>
                <p className="text-gray-500 text-sm">Responded</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Inquiries List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-800">All Inquiries</h2>
              </div>
              
              <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
                {inquiries.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No inquiries yet</p>
                  </div>
                ) : (
                  inquiries.map((inquiry) => (
                    <button
                      key={inquiry.id}
                      onClick={() => setSelectedInquiry(inquiry)}
                      className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                        selectedInquiry?.id === inquiry.id ? 'bg-blue-50 border-l-4 border-l-[#0066CC]' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="font-medium text-gray-800 truncate">{inquiry.name}</p>
                        {getStatusBadge(inquiry.status)}
                      </div>
                      <p className="text-sm text-gray-600 truncate mb-1">{inquiry.subject}</p>
                      <p className="text-xs text-gray-400">{formatDate(inquiry.created_at)}</p>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Inquiry Detail */}
          <div className="lg:col-span-2">
            {selectedInquiry ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-gray-800 mb-1">{selectedInquiry.subject}</h2>
                      <p className="text-gray-500 text-sm">{formatDate(selectedInquiry.created_at)}</p>
                    </div>
                    {getStatusBadge(selectedInquiry.status)}
                  </div>
                </div>
                
                <div className="p-6">
                  {/* Contact Info */}
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <User className="w-5 h-5 text-[#0066CC]" />
                      <div>
                        <p className="text-xs text-gray-500">Name</p>
                        <p className="font-medium text-gray-800">{selectedInquiry.name}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Mail className="w-5 h-5 text-[#0066CC]" />
                      <div>
                        <p className="text-xs text-gray-500">Email</p>
                        <a href={`mailto:${selectedInquiry.email}`} className="font-medium text-[#0066CC] hover:underline">
                          {selectedInquiry.email}
                        </a>
                      </div>
                    </div>
                    
                    {selectedInquiry.phone && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <Phone className="w-5 h-5 text-[#0066CC]" />
                        <div>
                          <p className="text-xs text-gray-500">Phone</p>
                          <a href={`tel:${selectedInquiry.phone}`} className="font-medium text-gray-800">
                            {selectedInquiry.phone}
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {selectedInquiry.company && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <Building className="w-5 h-5 text-[#0066CC]" />
                        <div>
                          <p className="text-xs text-gray-500">Company</p>
                          <p className="font-medium text-gray-800">{selectedInquiry.company}</p>
                        </div>
                      </div>
                    )}
                    
                    {selectedInquiry.service && (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg md:col-span-2">
                        <Briefcase className="w-5 h-5 text-[#0066CC]" />
                        <div>
                          <p className="text-xs text-gray-500">Service Interested In</p>
                          <p className="font-medium text-gray-800 capitalize">
                            {selectedInquiry.service.replace(/-/g, ' ')}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Message */}
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-800 mb-3">Message</h3>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedInquiry.message}</p>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-500 mr-2 self-center">Update Status:</p>
                    <Button
                      onClick={() => updateStatus(selectedInquiry.id, 'new')}
                      variant={selectedInquiry.status === 'new' ? 'default' : 'outline'}
                      size="sm"
                      className={selectedInquiry.status === 'new' ? 'bg-blue-600' : ''}
                    >
                      <Clock className="w-4 h-4 mr-1" />
                      New
                    </Button>
                    <Button
                      onClick={() => updateStatus(selectedInquiry.id, 'read')}
                      variant={selectedInquiry.status === 'read' ? 'default' : 'outline'}
                      size="sm"
                      className={selectedInquiry.status === 'read' ? 'bg-yellow-600' : ''}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Read
                    </Button>
                    <Button
                      onClick={() => updateStatus(selectedInquiry.id, 'responded')}
                      variant={selectedInquiry.status === 'responded' ? 'default' : 'outline'}
                      size="sm"
                      className={selectedInquiry.status === 'responded' ? 'bg-green-600' : ''}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Responded
                    </Button>
                    
                    <div className="flex-1" />
                    
                    <Button
                      onClick={() => window.open(`mailto:${selectedInquiry.email}?subject=Re: ${selectedInquiry.subject}`)}
                      className="bg-[#D20000] hover:bg-[#B00000]"
                      size="sm"
                    >
                      <Mail className="w-4 h-4 mr-1" />
                      Reply via Email
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-600 mb-2">Select an Inquiry</h3>
                <p className="text-gray-400">Click on an inquiry from the list to view details</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Admin;
